import { useEffect, useRef, useState } from "react";
import {
  MenuListItem,
  Input,
  IconArrowDropUp,
  IconArrowDropDown,
  KEY_VALUES,
  MENU_PLACEMENT,
  Autocomplete,
} from "@cdkglobal/radial";
import { IOption } from "../../types";
import { iconSpinner } from "./icons";
import { NO_RESULTS_DEFAULT_IOPTION } from "../LandingPage/constants";
import { useIntl } from "react-intl";

const CustomDropdown = ({
  id,
  options,
  label,
  isRequired,
  selectedOption,
  customStyle,
  onChange,
  hasError,
  isLoading,
  isDisabled,
}: {
  options: IOption[];
  id: string;
  label: string;
  onChange: (
    option: IOption,
    drdwnName?: string,
    deptType?: string,
    setterFunc?: () => void,
  ) => void;
  selectedOption: IOption;
  handleClear: (id: string) => void;
  isRequired: boolean;
  customStyle?: React.CSSProperties;
  hasError?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
}): JSX.Element => {
  const intl = useIntl();
  const targetRef = useRef<HTMLInputElement>(null);
  const dimensionRef = useRef<HTMLLabelElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [values, setValues] = useState(options);
  const [selectedValue, setSelectedValue] = useState(selectedOption);
  const [inputValue, setInputValue] = useState(selectedOption.label);
  const { ENTER, SPACE, TAB } = KEY_VALUES;

  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    if (targetRef.current) {
      setIsOpen(false);
    }
  };

  const handleSelect = (option: IOption) => {
    setSelectedValue(option);
    setInputValue(option.label);
    setValues(options);
    handleClose();
    (targetRef.current as HTMLInputElement).focus();
    onChange(option);
  };

  const findOptions = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputText = event.target.value;
    setInputValue(inputText);
    setIsOpen(true);

    const results = options.filter((row) => {
      return row.label.toLowerCase().includes(inputText.toLowerCase());
    });
    setValues(results);
    if (results.length === 0) {
      setValues([NO_RESULTS_DEFAULT_IOPTION()]);
    }
  };

  // const handleClearInput = (
  //   event:
  //     | React.PointerEvent<HTMLButtonElement>
  //     | React.KeyboardEvent<HTMLButtonElement>,
  // ) => {
  //   event.preventDefault();
  //   handleClear(id);
  //   setValues(options);
  // };

  const handleMenu = () => {
    setIsOpen((isOpen) => !isOpen);
    setValues(options);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === ENTER) {
      if (inputValue.length > 0 && values.length > 0) {
        setSelectedValue(values[0]);
        setInputValue(values[0].label);
        handleMenu();
      }
    }
    if (e.key === SPACE) {
      handleMenu();
    }
    if (e.key === TAB && selectedValue.label === "") {
      setInputValue("");
      setValues(options);
    }
  };

  useEffect(() => {
    setValues(
      options && options.length > 0 ? options : [NO_RESULTS_DEFAULT_IOPTION()],
    );
  }, [options]);

  useEffect(() => {
    setSelectedValue(selectedOption);
    setInputValue(selectedOption.label);
  }, [selectedOption]);

  let iconElement;

  if (isLoading) {
    iconElement = <img src={iconSpinner} alt="spinner" />;
  } else if (isOpen) {
    iconElement = <IconArrowDropUp />;
  } else {
    iconElement = <IconArrowDropDown />;
  }

  return (
    <>
      <div
        style={{ minWidth: "28%", ...customStyle }}
        data-testid={`dropdown-${id}`}
      >
        <Input
          autoComplete="off"
          dataTestId="menu-input"
          // hasClearButton={selectedOption.label !== "" && !isDisabled}
          // helperText=""
          icon={iconElement}
          id={id}
          label={label}
          labelRef={dimensionRef}
          name={id}
          onChange={() => {
            // Do nothing
          }}
          onClick={handleMenu}
          placeholder={intl.formatMessage({
            id: "dropdown.placeholder",
            defaultMessage: "Search...",
          })}
          ref={targetRef}
          value={inputValue}
          // onInputClear={handleClearInput}
          onKeyDown={handleKeyDown}
          style={{ caretColor: "transparent" }}
          isRequired={isRequired}
          hasError={hasError}
          errorMessage={intl.formatMessage(
            {
              id: "dropdown.pleaseSelectLabel",
              defaultMessage: "Please select {label}",
            },
            { label },
          )}
          enableCustomValidation
          isDisabled={isDisabled}
        />
      </div>
      <Autocomplete
        dataTestId="menu-container"
        dimensionRef={dimensionRef}
        labelRef={targetRef}
        onClose={handleClose}
        onChange={findOptions}
        onOpen={handleOpen}
        isOpen={isOpen}
        isAuto={false}
        placement={MENU_PLACEMENT.BOTTOM_CENTER}
        style={{
          zIndex: 40,
          width: dimensionRef?.current?.offsetWidth,
          maxWidth: dimensionRef?.current?.offsetWidth,
        }}
        onUnselect={selectedValue.label === ""}
      >
        {values?.map((val: IOption, ind: number) => (
          <MenuListItem
            title={val.label}
            dataTestId={`menu-item-${ind}`}
            isSelected={val.label === selectedOption.label}
            isDisabled={val.disabled}
            key={val.value}
            onClick={() => {
              handleSelect(val);
            }}
            hideSelectedItemCheckmark={false}
          >
            {val.label}
          </MenuListItem>
        ))}
      </Autocomplete>
    </>
  );
};

export default CustomDropdown;
